import { Button, Fieldset, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCloudUpload,
  IconDownload,
  IconUserPlus,
} from "@tabler/icons-react";
import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "src/pages/components/Popup";
import { getI18t } from "src/pages/i18n/i18n";
import AddManagedUser from "src/pages/in-app/managed-user-list/AddManagedUser";
import useIsMobile from "src/pages/utils/isMobileHook";
import {
  nullToastUpdateOptions,
  toastCommonError,
  toastDeviceResponseReport,
} from "src/pages/utils/ToastUtils";
import Device from "src/services/core/device/models/Device";
import CombinedManagedUser from "src/services/core/device/models/dto/CombinedManagedUser";
import physicalDeviceService from "src/services/core/device/PhysicalDevice.service";
import managedUserService from "src/services/core/managed-user/ManagedUser.service";
import {
  AddManagedUserDto,
  UpdateManagedUserDto,
} from "src/services/core/managed-user/models/ManagedUserDto";
import UploadFingerprintDto from "src/services/core/managed-user/models/UploadFingerprintDto";
import { physicalDeviceTimeWindowGroupsService } from "src/services/core/physical-device/time-window-group/PhysicalDeviceTimeWindowGroups.service";

const buttonClassname = "p-0 mx-2 mb-2";
const legendClassname = "m-1 flex flex-wrap items-center justify-center";
const legendtextClassname = "mx-1 text-sm my-0";

type ButtonsProps = {
  device: Device;
  combinedList?: CombinedManagedUser[];
  loadUsersOnDevice: () => Promise<void>;
  existingIdOnDeviceList: number[];
};
function DeviceDetailButtons({
  device,
  combinedList,
  loadUsersOnDevice,
  existingIdOnDeviceList,
}: ButtonsProps): ReactNode {
  const [isMobile] = useIsMobile();
  const t = getI18t(
    useTranslation(undefined, { keyPrefix: "inApp.deviceDetail" }).t
  );
  const [uploadModalOpen, { open: openUploadModal, close: closeUploadModal }] =
    useDisclosure(false);
  const [
    downloadModalOpen,
    { open: openDownloadModal, close: closeDownloadModal },
  ] = useDisclosure(false);
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);
  const [
    uploadTimeWindowGroupsModalOpen,
    {
      open: openUploadTimeWindowGroupsModal,
      close: closeUploadTimeWindowGroupsModal,
    },
  ] = useDisclosure(false);
  const [
    downloadTimeWindowGroupsModalOpen,
    {
      open: openDownloadTimeWindowGroupsModal,
      close: closeDownloadTimeWindowGroupsModal,
    },
  ] = useDisclosure(false);

  const getDownloadList = useCallback(() => {
    const conflictList: number[] = [];
    const matchingList: number[] = [];
    const serverOnlyList: number[] = [];
    combinedList?.forEach((u) => {
      if (u.managedUser != null) {
        if (u.status == "matching") {
          matchingList.push(u.managedUser.id);
        } else if (u.status == "different") {
          conflictList.push(u.managedUser.id);
        } else if (u.status == "server-only") {
          serverOnlyList.push(u.managedUser.id);
        }
      }
    });
    return { conflictList, matchingList, serverOnlyList };
  }, [combinedList]);

  const downloadToDevice = useCallback(
    async ({
      conflictList,
      matchingList,
      serverOnlyList,
    }: {
      conflictList: number[];
      matchingList: number[];
      serverOnlyList: number[];
    }) => {
      closeDownloadModal();
      const toastId = toast.loading(t("toast.sendingCommand"), {
        closeButton: true,
        closeOnClick: true,
      });
      // remove because server can also add fingerprints
      // if (conflictList.length == 0 && serverOnlyList.length == 0) {
      //   toast.update(toastId, {
      //     ...nullToastUpdateOptions,
      //     render: t("deviceUpToDate"),
      //     autoClose: 4000,
      //     type: "info",
      //   });
      //   return;
      // }
      const result = await physicalDeviceService.addAllUsersToDevice(
        device.serialNumber,
        matchingList
      );
      if (!result.success) {
        toastCommonError(result.code, toastId);
        return;
      }
      await loadUsersOnDevice();
      if (result.value.okSnList?.length == 1) {
        toast.update(toastId, {
          ...nullToastUpdateOptions,
          render: t("uploadSuccess"),
          type: "success",
        });
      } else {
        toastDeviceResponseReport([device], result.value, toastId);
      }
    },
    [loadUsersOnDevice, t]
  );

  const getUploadList = useCallback(() => {
    const added: AddManagedUserDto[] = [];
    const updated: UpdateManagedUserDto[] = [];
    const matchList: UpdateManagedUserDto[] = [];
    combinedList?.forEach((u) => {
      if (u.managedUserOnDevice != null) {
        if (u.status == "different") {
          updated.push({
            ...u.managedUserOnDevice,
            id: u.managedUser.id,
            deviceId: u.managedUserOnDevice.idOnDevice,
            internalId: "",
            imageUrl: "",
          });
        } else if (u.status == "matching") {
          // the matching managed users need to be uploaded so that their fingerprints can also be uploaded
          // TODO: find an alternative way to resolve this (the fingerprintMarks field is bugged and is
          // currently providing the number of fingerprints for device managed users)
          matchList.push(u.managedUser);
        } else if (u.status == "device-only") {
          added.push({
            ...u.managedUserOnDevice,
            deviceId: u.managedUserOnDevice.idOnDevice,
            internalId: "",
            imageUrl: "",
          });
        }
      }
    });
    return { matchList, added, updated };
  }, [loadUsersOnDevice, combinedList]);

  const uploadToServer = useCallback(
    async ({
      added,
      updated,
      matchList,
    }: {
      added: AddManagedUserDto[];
      updated: UpdateManagedUserDto[];
      matchList: UpdateManagedUserDto[];
    }) => {
      closeUploadModal();
      const toastId = toast.loading(t("toast.sendingCommand"), {
        closeButton: true,
        closeOnClick: true,
      });
      // maybe
      // const hasData = added.length != 0 || updated.length != 0;
      // if (!hasData) {
      //   toast.update(toastId, {
      //     ...nullToastUpdateOptions,
      //     render: t("serverUpToDate"),
      //     autoClose: 4000,
      //     type: "info",
      //   });
      //   return;
      // }
      const result = await managedUserService.addUpdateManagedUser(
        device.groupId,
        added,
        updated,
        device.serialNumber,
        matchList.map(
          (m): UploadFingerprintDto => ({
            managedUserId: m.id,
            idOnDevice: m.deviceId,
          })
        )
      );
      await managedUserService.getManagedUserList(
        device.groupId,
        false,
        true,
        true
      );
      if (!result.success) {
        toastCommonError(result.code, toastId);
      } else {
        toast.update(toastId, {
          ...nullToastUpdateOptions,
          render: t("uploadSuccess"),
          type: "success",
        });
        if (result.value?.report != null) {
            toastDeviceResponseReport([device], result.value.report);
        }
      }
    },
    [t]
  );
  const uploadTimeWindowGroupsToServer = useCallback(async () => {
    closeUploadModal();
    const toastId = toast.loading(t("toast.sendingCommand"), {
      closeButton: true,
      closeOnClick: true,
    });
    const result = await physicalDeviceTimeWindowGroupsService.upload(
      device.serialNumber
    );
    if (!result.success) {
      toastCommonError(result.code, toastId);
    } else {
      toast.update(toastId, {
        ...nullToastUpdateOptions,
        render: t("uploadTimeWindowGroupsSuccess"),
        type: "success",
      });
      closeUploadTimeWindowGroupsModal();
    }
  }, [t]);
  const downloadTimeWindowGroupsToDevice = useCallback(async () => {
    closeDownloadModal();
    const toastId = toast.loading(t("toast.sendingCommand"), {
      closeButton: true,
      closeOnClick: true,
    });
    const result = await physicalDeviceTimeWindowGroupsService.download(
      device.serialNumber
    );
    if (!result.success) {
      toastCommonError(result.code, toastId);
    } else {
      toast.update(toastId, {
        ...nullToastUpdateOptions,
        render: t("downloadTimeWindowGroupsSuccess"),
        type: "success",
      });
      closeDownloadTimeWindowGroupsModal();
    }
  }, [t]);
  return (
    <div className="mx-1 my-5 flex justify-center w-full flex-wrap">
      <Fieldset
        className={legendClassname}
        legend={
          <h2 className={legendtextClassname}>{t("managedUserLegend")}</h2>
        }
      >
        <Button
          color="green"
          onClick={openAddModal}
          leftSection={<IconUserPlus size={20} stroke={2.5} />}
          className={buttonClassname}
          disabled={combinedList == null}
        >
          {t("addManagedUser")}
        </Button>
        <Button
          color="blue"
          leftSection={<IconDownload size={20} />}
          className={buttonClassname}
          onClick={openDownloadModal}
          disabled={combinedList == null}
        >
          {t("download")}
        </Button>
        <Button
          color="blue"
          onClick={openUploadModal}
          leftSection={<IconCloudUpload size={20} stroke={2.5} />}
          className={buttonClassname}
          disabled={combinedList == null}
        >
          {t("upload")}
        </Button>
      </Fieldset>
      <Fieldset
        legend={
          <h2 className={legendtextClassname}>{t("timeWindowGroupLegend")}</h2>
        }
        className={legendClassname}
      >
        <Button
          color="blue"
          leftSection={<IconDownload size={20} />}
          className={buttonClassname}
          onClick={openDownloadTimeWindowGroupsModal}
          disabled={combinedList == null}
        >
          {t("download")}
        </Button>
        <Button
          color="blue"
          onClick={openUploadTimeWindowGroupsModal}
          leftSection={<IconCloudUpload size={20} stroke={2.5} />}
          className={buttonClassname}
          disabled={combinedList == null}
        >
          {t("upload")}
        </Button>
      </Fieldset>
      <Popup
        prepare={getDownloadList}
        onMainAction={downloadToDevice}
        titleFactory={(value) =>
          t("downloadTitle", {
            count: Math.min(
              value.conflictList.length + value.serverOnlyList.length,
              1000 - value.matchingList.length
            ),
          })
        }
        childrenFactory={(value) =>
          t("downloadWarning", { count: value.conflictList.length })
        }
        acceptNode={t("confirmSync")}
        acceptColor={"blue"}
        onClose={closeDownloadModal}
        opened={downloadModalOpen}
      />
      <Popup
        prepare={getUploadList}
        onMainAction={uploadToServer}
        titleFactory={(value) =>
          t("uploadTitle", {
            count: value.added.length + value.updated.length,
          })
        }
        childrenFactory={(value) =>
          t("uploadWarning", { count: value.updated.length })
        }
        acceptNode={t("confirmSync")}
        acceptColor={"blue"}
        onClose={closeUploadModal}
        opened={uploadModalOpen}
      />
      <Popup
        prepare={undefined}
        onMainAction={downloadTimeWindowGroupsToDevice}
        titleFactory={() => t("downloadTimeWindowGroupTitle")}
        childrenFactory={() => t("downloadTimeWindowGroupWarning")}
        acceptNode={t("confirmSync")}
        acceptColor={"blue"}
        onClose={closeDownloadTimeWindowGroupsModal}
        opened={downloadTimeWindowGroupsModalOpen}
      />
      <Popup
        prepare={undefined}
        onMainAction={uploadTimeWindowGroupsToServer}
        titleFactory={() => t("uploadTimeWindowGroupTitle")}
        childrenFactory={() => t("uploadTimeWindowGroupWarning")}
        acceptNode={t("confirmSync")}
        acceptColor={"blue"}
        onClose={closeUploadTimeWindowGroupsModal}
        opened={uploadTimeWindowGroupsModalOpen}
      />
      <Modal
        opened={addModalOpened}
        onClose={closeAddModal}
        fullScreen={isMobile}
      >
        <AddManagedUser
          snList={[]}
          onSuccess={closeAddModal}
          existingIdList={existingIdOnDeviceList}
        />
      </Modal>
    </div>
  );
}

export default DeviceDetailButtons;
